import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  X, ChevronLeft, ChevronRight, ExternalLink, Download,
  CheckCircle2, Send, Pencil, Loader2, FileText, Trash2,
  Building2, Calendar, Hash, Euro, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_STYLE = {
  'À vérifier':        'bg-amber-100 text-amber-700',
  'Validée':           'bg-blue-100 text-blue-700',
  'Poussée Pennylane': 'bg-emerald-100 text-emerald-700',
};

const fmt = (v) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

/**
 * Panneau latéral d'aperçu d'une facture d'achat.
 * - Iframe PDF (zoom/pan natifs du navigateur)
 * - Boutons Précédent/Suivant + raccourcis clavier ← / →
 * - Actions rapides : Valider, Push Pennylane (via endpoint bulk async pour éviter les timeouts Cloudflare), Modifier, Supprimer
 */
export default function FactureAchatPreviewDrawer({
  facture,             // objet facture d'achat (avec facture_achat_id, statut, etc.)
  allFactures,         // liste filtrée courante pour prev/next
  onClose,             // () => void
  onNavigate,          // (nextFacture) => void
  onChanged,           // () => void — appelé après modification pour recharger la liste
  onOpenDetail,        // (id) => void
}) {
  const [pdfError, setPdfError] = useState(false);
  const [updatingStatut, setUpdatingStatut] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Cherche l'index et calcule les voisins
  const { prev, next, index } = useMemo(() => {
    if (!facture || !allFactures?.length) return { prev: null, next: null, index: -1 };
    const idx = allFactures.findIndex((f) => f.facture_achat_id === facture.facture_achat_id);
    return {
      index: idx,
      prev: idx > 0 ? allFactures[idx - 1] : null,
      next: idx >= 0 && idx < allFactures.length - 1 ? allFactures[idx + 1] : null,
    };
  }, [facture, allFactures]);

  // Raccourcis clavier ← / → / Esc
  useEffect(() => {
    if (!facture) return;
    const handler = (e) => {
      if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === 'ArrowLeft' && prev) onNavigate(prev);
      else if (e.key === 'ArrowRight' && next) onNavigate(next);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [facture, prev, next, onNavigate, onClose]);

  useEffect(() => {
    setPdfError(false);
  }, [facture?.facture_achat_id]);

  if (!facture) return null;

  const isImage = facture.filename && /\.(jpg|jpeg|png|webp|heic|heif)$/i.test(facture.filename);
  const pdfUrl = `${BACKEND_URL}/api/factures-achat/${facture.facture_achat_id}/pdf`;

  const handleValidate = async () => {
    setUpdatingStatut(true);
    try {
      await api.put(`/factures-achat/${facture.facture_achat_id}`, { statut: 'Validée' });
      toast.success('Facture validée');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la validation');
    } finally {
      setUpdatingStatut(false);
    }
  };

  const handlePush = async () => {
    setPushing(true);
    try {
      // On route via l'endpoint bulk (background task) pour éviter les timeouts
      // Cloudflare (502 "invalid or incomplete response") sur les gros PDF.
      await api.post('/factures-achat/bulk/push-pennylane', {
        ids: [facture.facture_achat_id],
      });
      toast.success('Envoi Pennylane en cours…', { duration: 4000 });
      // Poll rapide pour rafraîchir dès que fini
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        try {
          const r = await api.get('/factures-achat/bulk/push-pennylane/progress');
          const p = r.data;
          if (!p?.running) {
            clearInterval(iv);
            setPushing(false);
            if ((p?.pushed || 0) > 0 && (p?.failed || 0) === 0) {
              toast.success('Facture poussée vers Pennylane ✓', { duration: 6000 });
            } else if ((p?.failed || 0) > 0) {
              const err = p.errors?.[0]?.error || 'Erreur inconnue';
              toast.error(`Échec Pennylane : ${err}`, { duration: 12000 });
            }
            onChanged?.();
          }
        } catch (_e) { /* ignore */ }
        if (tries > 60) { clearInterval(iv); setPushing(false); }
      }, 2000);
    } catch (e) {
      setPushing(false);
      toast.error(e?.response?.data?.detail || "Impossible de lancer l'envoi");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer cette facture ?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/factures-achat/${facture.facture_achat_id}`);
      toast.success('Facture supprimée');
      // Navigate to next (or prev, or close)
      const target = next || prev;
      if (target) onNavigate(target); else onClose();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40"
        onClick={onClose}
        data-testid="fa-preview-backdrop"
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 h-full w-full md:w-[64%] lg:w-[56%] bg-white shadow-2xl z-50 flex flex-col"
        data-testid="fa-preview-drawer"
        role="dialog"
        aria-label="Aperçu facture d'achat"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => prev && onNavigate(prev)}
              disabled={!prev}
              className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Précédente (←)"
              data-testid="fa-preview-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => next && onNavigate(next)}
              disabled={!next}
              className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Suivante (→)"
              data-testid="fa-preview-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <span className="text-xs text-slate-500 ml-2 tabular-nums shrink-0">
              {index + 1} / {allFactures.length}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100"
            title="Fermer (Esc)"
            data-testid="fa-preview-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Metadata bar */}
        <div className="border-b border-slate-100 px-4 py-3 shrink-0 bg-slate-50/50">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h2 className="text-lg font-bold text-slate-900 truncate">
                  {facture.fournisseur?.nom || facture.filename || '—'}
                </h2>
                <Badge className={STATUT_STYLE[facture.statut] || 'bg-slate-100 text-slate-700'}>
                  {facture.statut}
                </Badge>
                {facture.pennylane_invoice_id && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">
                    Pennylane · {facture.pennylane_invoice_id}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                {facture.numero_facture && (
                  <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" />{facture.numero_facture}</span>
                )}
                {facture.date_facture && (
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{facture.date_facture}</span>
                )}
                {facture.fournisseur?.siret && (
                  <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />SIRET {facture.fournisseur.siret}</span>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-2xl font-bold text-slate-900 tabular-nums">
                <Euro className="w-4 h-4 inline text-slate-400" /> {fmt(facture.montants?.total_ttc)}
              </p>
              <p className="text-xs text-slate-400">TTC</p>
            </div>
          </div>
        </div>

        {/* Actions bar */}
        <div className="border-b border-slate-200 px-4 py-2.5 shrink-0 flex items-center gap-2 flex-wrap bg-white">
          {facture.statut === 'À vérifier' && (
            <Button
              size="sm"
              onClick={handleValidate}
              disabled={updatingStatut}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="fa-preview-validate-btn"
            >
              {updatingStatut
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />…</>
                : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Valider</>}
            </Button>
          )}
          {facture.statut === 'Validée' && !facture.pennylane_invoice_id && (
            <Button
              size="sm"
              onClick={handlePush}
              disabled={pushing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="fa-preview-push-btn"
            >
              {pushing
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Envoi…</>
                : <><Send className="w-3.5 h-3.5 mr-1" />Envoyer vers Pennylane</>}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onOpenDetail?.(facture.facture_achat_id)}
            data-testid="fa-preview-edit-btn"
          >
            <Pencil className="w-3.5 h-3.5 mr-1" />Modifier en détail
          </Button>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
            data-testid="fa-preview-newtab-btn"
          >
            <ExternalLink className="w-3.5 h-3.5" />Ouvrir dans un onglet
          </a>
          <a
            href={pdfUrl}
            download={facture.filename || `${facture.facture_achat_id}.pdf`}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
            data-testid="fa-preview-download-btn"
          >
            <Download className="w-3.5 h-3.5" />Télécharger
          </a>
          <div className="flex-1" />
          {!facture.pennylane_invoice_id && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-600 hover:bg-red-50"
              data-testid="fa-preview-delete-btn"
            >
              {deleting
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Trash2 className="w-3.5 h-3.5" />}
            </Button>
          )}
        </div>

        {/* PDF Viewer (native browser zoom/pan) */}
        <div className="flex-1 bg-slate-900 overflow-hidden">
          {pdfError ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-2 p-6 text-center">
              <AlertTriangle className="w-10 h-10 text-amber-400" />
              <p className="font-semibold">Impossible d&apos;afficher le fichier ici</p>
              <p className="text-sm text-slate-400">Cliquez sur « Ouvrir dans un onglet » pour le consulter dans votre navigateur.</p>
            </div>
          ) : isImage ? (
            <div className="h-full w-full overflow-auto flex items-center justify-center p-4">
              <img
                src={pdfUrl}
                alt={facture.filename}
                className="max-w-none"
                onError={() => setPdfError(true)}
                style={{ imageRendering: 'auto' }}
              />
            </div>
          ) : (
            <iframe
              key={facture.facture_achat_id}
              src={`${pdfUrl}#zoom=page-fit`}
              title="Aperçu facture"
              className="w-full h-full border-0 bg-white"
              onError={() => setPdfError(true)}
              data-testid="fa-preview-iframe"
            />
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400 flex items-center justify-between gap-3 shrink-0 bg-slate-50/50">
          <span className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {facture.filename || 'facture.pdf'}
          </span>
          <span>← / → pour naviguer · Esc pour fermer · zoom natif du navigateur</span>
        </div>
      </aside>
    </>
  );
}
